import React, { useState } from "react";
import { ArrowRight, CheckCircle2, LockKeyhole } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../services/api";

const ResetPassword = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (password.length < 8) {
      setError(t("resetPassword.passwordTooShort"));
      return;
    }

    if (password !== confirmPassword) {
      setError(t("resetPassword.passwordMismatch"));
      return;
    }

    if (!token) {
      setError(t("resetPassword.missingToken"));
      return;
    }

    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setSuccess(true);
      setTimeout(() => navigate("/login"), 1800);
    } catch (err) {
      setError(t("resetPassword.resetError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="page-shell flex min-h-[calc(100vh-80px)] items-center justify-center">
      <div className="grid w-full max-w-4xl overflow-hidden rounded-3xl border border-gold-100 bg-white shadow-xl lg:grid-cols-[0.85fr_1.15fr]">
        <aside className="relative hidden overflow-hidden bg-forest p-8 text-white lg:block">
          <div aria-hidden="true" className="absolute -right-12 -top-12 h-64 w-64 rounded-full border-[24px] border-gold-400/35" />
          <div aria-hidden="true" className="absolute -bottom-20 -left-20 h-80 w-80 rounded-full border-[32px] border-white/10" />
          <div className="relative">
            <p className="eyebrow text-gold-400">{t("resetPassword.eyebrow")}</p>
            <h1 className="mt-5 font-serif text-4xl font-bold leading-tight">{t("resetPassword.title")}</h1>
            <p className="mt-5 text-sm leading-7 text-green-100">
              {t("resetPassword.intro")}
            </p>
          </div>
        </aside>

        <section className="p-6 sm:p-10">
          <div className="mb-8 flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gold-50 text-gold-600">
              <LockKeyhole size={22} />
            </span>
            <div>
              <p className="eyebrow">{t("resetPassword.recovery")}</p>
              <h2 className="font-serif text-3xl font-bold text-forest">{t("resetPassword.heading")}</h2>
            </div>
          </div>

          {error && <p className="mb-5 rounded-xl border border-red-100 bg-red-50 p-3 text-sm text-red-700">{error}</p>}

          {success ? (
            <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-5 text-emerald-800">
              <div className="flex items-center gap-3">
                <CheckCircle2 size={20} />
                <p className="font-semibold">{t("resetPassword.updated")}</p>
              </div>
              <p className="mt-3 text-sm leading-6">{t("resetPassword.redirecting")}</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="label">{t("resetPassword.newPassword")}</label>
                <input
                  className="input"
                  type="password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder={t("resetPassword.passwordMin")}
                />
              </div>

              <div>
                <label className="label">{t("resetPassword.confirmPassword")}</label>
                <input
                  className="input"
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  placeholder={t("auth.signup.repeatPassword")}
                />
              </div>

              <button type="submit" className="btn-primary w-full" disabled={loading}>
                {loading ? t("resetPassword.updating") : t("resetPassword.update")} <ArrowRight size={16} />
              </button>

              <p className="text-center text-sm text-gray-500">
                <Link to="/login" className="font-semibold text-gold-700 hover:underline">{t("resetPassword.backToLogin")}</Link>
              </p>
            </form>
          )}
        </section>
      </div>
    </main>
  );
};

export default ResetPassword;
